import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaKey, FaGlobe, FaPalette, FaBell, FaShieldAlt } from 'react-icons/fa';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    siteName: 'The Tiny Times',
    siteTagline: 'Weekly fun stories, puzzles & learning',
    adminPassword: '',
    newPassword: '',
    confirmPassword: '',
    enableNotifications: true,
    maintenanceMode: false,
    analyticsId: '',
  });

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (settings.newPassword && settings.newPassword !== settings.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }
    alert('Settings saved successfully!');
  };

  const handlePasswordChange = () => {
    if (!settings.adminPassword || !settings.newPassword) {
      alert('Please fill in all password fields');
      return;
    }
    if (settings.newPassword !== settings.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }
    if (settings.newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    alert('Password changed successfully!');
    setSettings(prev => ({
      ...prev,
      adminPassword: '',
      newPassword: '',
      confirmPassword: '',
    }));
  };

  return (
    <div className="space-y-6">
      {/* General Settings */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary-green rounded-lg flex items-center justify-center">
            <FaGlobe className="text-white text-xl" />
          </div>
          <h3 className="text-xl font-display font-bold text-primary-green-dark">
            General Settings
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold text-primary-green-dark mb-2">
              Site Name
            </label>
            <input
              type="text"
              value={settings.siteName}
              onChange={(e) => handleChange('siteName', e.target.value)}
              className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
            />
          </div>

          <div>
            <label className="block font-semibold text-primary-green-dark mb-2">
              Site Tagline
            </label>
            <input
              type="text"
              value={settings.siteTagline}
              onChange={(e) => handleChange('siteTagline', e.target.value)}
              className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block font-semibold text-primary-green-dark mb-2">
              Google Analytics ID
            </label>
            <input
              type="text"
              value={settings.analyticsId}
              onChange={(e) => handleChange('analyticsId', e.target.value)}
              placeholder="G-XXXXXXXXXX"
              className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Password Settings */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-accent-orange rounded-lg flex items-center justify-center">
            <FaKey className="text-white text-xl" />
          </div>
          <h3 className="text-xl font-display font-bold text-primary-green-dark">
            Change Admin Password
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block font-semibold text-primary-green-dark mb-2">
              Current Password
            </label>
            <input
              type="password"
              value={settings.adminPassword}
              onChange={(e) => handleChange('adminPassword', e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
            />
          </div>

          <div>
            <label className="block font-semibold text-primary-green-dark mb-2">
              New Password
            </label>
            <input
              type="password"
              value={settings.newPassword}
              onChange={(e) => handleChange('newPassword', e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
            />
          </div>

          <div>
            <label className="block font-semibold text-primary-green-dark mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              value={settings.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
            />
          </div>
        </div>

        <motion.button
          onClick={handlePasswordChange}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="mt-4 flex items-center gap-2 px-6 py-2 bg-accent-orange text-white rounded-lg font-semibold"
        >
          <FaKey />
          <span>Update Password</span>
        </motion.button>
      </div>

      {/* Preferences */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
            <FaBell className="text-white text-xl" />
          </div>
          <h3 className="text-xl font-display font-bold text-primary-green-dark">
            Preferences
          </h3>
        </div>

        <div className="space-y-4">
          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
            <div className="flex items-center gap-3">
              <FaBell className="text-primary-green text-xl" />
              <div>
                <div className="font-semibold text-primary-green-dark">
                  Email Notifications
                </div>
                <div className="text-sm text-neutral-gray-dark">
                  Receive email updates about new enquiries
                </div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.enableNotifications}
              onChange={(e) => handleChange('enableNotifications', e.target.checked)}
              className="w-6 h-6 accent-primary-green"
            />
          </label>

          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
            <div className="flex items-center gap-3">
              <FaShieldAlt className="text-red-500 text-xl" />
              <div>
                <div className="font-semibold text-primary-green-dark">
                  Maintenance Mode
                </div>
                <div className="text-sm text-neutral-gray-dark">
                  Temporarily disable the website for visitors
                </div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.maintenanceMode}
              onChange={(e) => handleChange('maintenanceMode', e.target.checked)}
              className="w-6 h-6 accent-red-500"
            />
          </label>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="card border-2 border-red-200 bg-red-50">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
            <FaShieldAlt className="text-white text-xl" />
          </div>
          <h3 className="text-xl font-display font-bold text-red-600">
            Danger Zone
          </h3>
        </div>

        <p className="text-neutral-gray-dark mb-4">
          These actions are irreversible. Please be careful.
        </p>

        <div className="flex flex-wrap gap-3">
          <motion.button
            onClick={() => {
              if (window.confirm('Are you sure you want to reset all content to defaults?')) {
                localStorage.clear();
                window.location.reload();
              }
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
          >
            Reset All Content
          </motion.button>

          <motion.button
            onClick={() => {
              if (window.confirm('Clear all uploaded files from local storage?')) {
                localStorage.removeItem('tinytimes-samples');
                localStorage.removeItem('tinytimes-gallery');
                localStorage.removeItem('tinytimes-videos');
                window.location.reload();
              }
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-2 bg-red-100 text-red-600 rounded-lg font-semibold hover:bg-red-200 transition-colors"
          >
            Clear Uploaded Files
          </motion.button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <motion.button
          onClick={handleSave}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-primary flex items-center gap-2"
        >
          <FaSave />
          <span>Save All Settings</span>
        </motion.button>
      </div>
    </div>
  );
};

export default AdminSettings;